import type { Map as MapLibreMap } from "maplibre-gl";

import {
  DRAW_POLYLINE_MIN_POINTS,
  POLYLINE_EDIT_SIMPLIFY_TOLERANCE,
} from "@ryde/controls";
import { IPoint, TLatLngTuple } from "@ryde/shared";

import { MIN_LINE_POINTS } from "../route-block-maplibre.constants";

/** Perpendicular distance (px) from `point` to the line through `start`→`end`. */
const perpendicularDistance = (
  point: IPoint,
  start: IPoint,
  end: IPoint,
): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  return Math.abs(
    (dy * point.x - dx * point.y + end.x * start.y - end.y * start.x) / length,
  );
};

/**
 * Douglas-Peucker — drops points that don't change the shape (keeps only real
 * bends), not just points close together. Mirrors Leaflet's `L.LineUtil.simplify`,
 * so a mostly-straight route collapses to a few handles instead of a wall of dots.
 */
const douglasPeucker = (points: IPoint[], tolerance: number): IPoint[] => {
  if (points.length <= MIN_LINE_POINTS) {
    return points;
  }

  let maxDistance = 0;
  let index = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const distance = perpendicularDistance(
      points[i],
      points[0],
      points[points.length - 1],
    );
    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }

  if (maxDistance <= tolerance) {
    return [points[0], points[points.length - 1]];
  }

  return [
    ...douglasPeucker(points.slice(0, index + 1), tolerance).slice(0, -1),
    ...douglasPeucker(points.slice(index), tolerance),
  ];
};

/**
 * Simplify a polyline to a handful of shape-preserving handles (road-following
 * routes carry hundreds of points). Projected to pixels so the tolerance is
 * zoom-aware. Input/output are `[lat, lng]`.
 */
export const simplifyEditPositions = (
  map: MapLibreMap,
  positions: TLatLngTuple[],
): TLatLngTuple[] => {
  if (positions.length <= DRAW_POLYLINE_MIN_POINTS) {
    return positions;
  }

  const projected = positions.map(([lat, lng]) => map.project([lng, lat]));

  const simplified = douglasPeucker(
    projected,
    POLYLINE_EDIT_SIMPLIFY_TOLERANCE,
  ).map(point => {
    const lngLat = map.unproject([point.x, point.y]);
    return [lngLat.lat, lngLat.lng] as TLatLngTuple;
  });

  if (simplified.length >= DRAW_POLYLINE_MIN_POINTS) {
    return simplified;
  }

  // A near-straight route collapses to its two endpoints — below the save minimum,
  // so a later drag would be rejected and the edit lost. Fall back to evenly-spaced
  // handles sampled from the original so the edit always has enough points to save.
  const step = (positions.length - 1) / (DRAW_POLYLINE_MIN_POINTS - 1);
  return Array.from(
    { length: DRAW_POLYLINE_MIN_POINTS },
    (_, i) => positions[Math.round(i * step)],
  );
};
